import { Action, ActionCreator, Dispatch } from "redux";

import { ParentState } from "@/ProposalBuilder/store/reducers/Parent";
import { ThunkAction } from "redux-thunk";
import { ProposalBuilderState } from "@/ProposalBuilder/store";
import { Document, PaymentSummary } from "@/Contracts/Documents";
import { DocumentPageDetails } from "@/Contracts/Documents/Revision";
import {
	updateDocumentPageDetails,
	updateTemplatePageDetails
} from "@/ProposalBuilder/services/Block";
import { VendorTemplateDetail } from "@/Contracts/Vendor/Templates";
import { Template } from "@/Contracts/Templates";

export enum ParentStateActionTypes {
	UPDATE_PARENT_STATE = "UPDATE_PARENT_STATE",
	UPDATE_CURRENT_ACTIVE_DOCUMENT = "UPDATE_CURRENT_ACTIVE_DOCUMENT",
	UPDATE_CURRENT_ACTIVE_TEMPLATE = "UPDATE_CURRENT_ACTIVE_TEMPLATE",
	UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL = "UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL"
}

export interface UpdateParentStateAction
	extends Action<ParentStateActionTypes.UPDATE_PARENT_STATE> {
	readonly parentState: ParentState;
}

export interface UpdateCurrentActiveDocumentAction
	extends Action<ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_DOCUMENT> {
	readonly currentActiveDocument: Document;
}

export interface UpdateCurrentActiveTemplateAction
	extends Action<ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_TEMPLATE> {
	readonly currentActiveTemplate: Template | VendorTemplateDetail | null;
}

export interface UpdateCurrentDocPaymentSummarySuccessfulAction
	extends Action<ParentStateActionTypes.UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL> {
	readonly paymentSummary: PaymentSummary | null;
}

export type ParentStateActions =
	| UpdateParentStateAction
	| UpdateCurrentActiveDocumentAction
	| UpdateCurrentActiveTemplateAction
	| UpdateCurrentDocPaymentSummarySuccessfulAction;

export interface ParentStateDispatch {
	updateParentState: ActionCreator<
		ThunkAction<
			UpdateParentStateAction,
			ProposalBuilderState,
			ParentState,
			UpdateParentStateAction
		>
	>;
	updateCurrentActiveDocument: ActionCreator<
		ThunkAction<
			UpdateCurrentActiveDocumentAction,
			ProposalBuilderState,
			Document,
			UpdateCurrentActiveDocumentAction
		>
	>;
	updateDocumentPaymentSummary: ActionCreator<
		ThunkAction<
			UpdateCurrentDocPaymentSummarySuccessfulAction,
			ProposalBuilderState,
			PaymentSummary | null,
			UpdateCurrentDocPaymentSummarySuccessfulAction
		>
	>;
	updateDocumentPageDetails: ActionCreator<
		ThunkAction<
			Promise<UpdateCurrentActiveDocumentAction>,
			ProposalBuilderState,
			DocumentPageDetails,
			UpdateCurrentActiveDocumentAction
		>
	>;
	updateTemplatePageDetails: ActionCreator<
		ThunkAction<
			Promise<UpdateCurrentActiveTemplateAction>,
			ProposalBuilderState,
			DocumentPageDetails,
			UpdateCurrentActiveTemplateAction
		>
	>;
}

export const ParentActionCreators: ParentStateDispatch = {
	updateParentState:
		(parentState: ParentState) =>
		(dispatch: Dispatch): UpdateParentStateAction => {
			const updateParentStateAction: UpdateParentStateAction = {
				type: ParentStateActionTypes.UPDATE_PARENT_STATE,
				parentState
			};
			return dispatch(updateParentStateAction);
		},
	updateCurrentActiveDocument:
		(currentActiveDocument: Document) =>
		(dispatch: Dispatch): UpdateCurrentActiveDocumentAction => {
			const updateCurrentActiveDocumentAction: UpdateCurrentActiveDocumentAction =
				{
					type: ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_DOCUMENT,
					currentActiveDocument
				};
			return dispatch(updateCurrentActiveDocumentAction);
		},
	updateDocumentPaymentSummary: (paymentSummary: PaymentSummary | null) => {
		return (
			dispatch: Dispatch
		): UpdateCurrentDocPaymentSummarySuccessfulAction => {
			const updateCurrentDocPaymentSummarySuccessfulAction: UpdateCurrentDocPaymentSummarySuccessfulAction =
				{
					type: ParentStateActionTypes.UPDATE_CURRENT_DOC_PAYMENT_SUMMARY_SUCCESSFUL,
					paymentSummary
				};
			return dispatch(updateCurrentDocPaymentSummarySuccessfulAction);
		};
	},
	updateDocumentPageDetails:
		(pageDetails: DocumentPageDetails, documentId) =>
		async (
			dispatch: Dispatch,
			getState: () => ProposalBuilderState
		): Promise<UpdateCurrentActiveDocumentAction> => {
			const currentActiveDocument =
				getState().parentState.currentActiveDocument;
			if (currentActiveDocument?.revisions?.[0]) {
				currentActiveDocument.revisions[0].page_details = pageDetails;
				const updateCurrentActiveDocumentAction: UpdateCurrentActiveDocumentAction =
					{
						type: ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_DOCUMENT,
						currentActiveDocument
					};
				dispatch(updateCurrentActiveDocumentAction);
			}
			const document = await updateDocumentPageDetails(
				{
					page_detail: pageDetails
				},
				documentId
			);
			const updateCurrentActiveDocumentAction: UpdateCurrentActiveDocumentAction =
				{
					type: ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_DOCUMENT,
					currentActiveDocument: document
				};
			return dispatch(updateCurrentActiveDocumentAction);
		},
	updateTemplatePageDetails:
		(pageDetails: DocumentPageDetails, templateId) =>
		async (
			dispatch: Dispatch,
			getState: () => ProposalBuilderState
		): Promise<UpdateCurrentActiveTemplateAction> => {
			const currentActiveTemplate =
				getState().parentState.currentActiveTemplate;
			if (currentActiveTemplate) {
				currentActiveTemplate.page_details = pageDetails;
				const updateCurrentActiveDocumentAction: UpdateCurrentActiveTemplateAction =
					{
						type: ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_TEMPLATE,
						currentActiveTemplate
					};
				dispatch(updateCurrentActiveDocumentAction);
			}
			const template = await updateTemplatePageDetails(
				{
					page_detail: pageDetails
				},
				templateId
			);
			const updateCurrentActiveDocumentAction: UpdateCurrentActiveTemplateAction =
				{
					type: ParentStateActionTypes.UPDATE_CURRENT_ACTIVE_TEMPLATE,
					currentActiveTemplate: template
				};
			return dispatch(updateCurrentActiveDocumentAction);
		}
};

export default ParentActionCreators;
